import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { 
  FaBuilding, 
  FaEdit, 
  FaTrash, 
  FaPlus, 
  FaUsers, 
  FaFolder, 
  FaClipboardList,
  FaArrowRight,
  FaCheck,
  FaTimes,
  FaSearch,
  FaFilter,
  FaDownload,
  FaSave,
  FaExclamationTriangle,
  FaArrowLeft,
  FaTachometerAlt
} from 'react-icons/fa';
import './Admin.css';

const OrganizationManagement = () => {
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [view, setView] = useState('list'); // list, detail, folders, templates, users
  const [orgFolders, setOrgFolders] = useState([]);
  const [orgTemplates, setOrgTemplates] = useState([]);
  const [orgUsers, setOrgUsers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    industry: 'all',
    status: 'all',
    size: 'all'
  });
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    size: 'Small (1-50)',
    website: '',
    phone: '',
    primaryContact: {
      name: '',
      email: '',
      phone: ''
    },
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
      country: ''
    },
    subscriptionTier: 'Basic',
    subscriptionStatus: 'active',
    maxUsers: 5
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [orgToDelete, setOrgToDelete] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Create refs for form fields
  const formRef = useRef(null);

  useEffect(() => {
    fetchOrganizations();
    fetchStats();
  }, []);

  // Apply filters and search whenever organizations, searchTerm, or filters change
  useEffect(() => {
    applyFiltersAndSearch();
  }, [organizations, searchTerm, filters]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/organizations');
      setOrganizations(res.data);
      setFilteredOrganizations(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching organizations:', err);
      setError('Failed to load organizations. Please try again.');
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get('/api/admin/stats');
      setStats({
        total: res.data.organizationCount || 0,
        active: res.data.activeOrganizationCount || 0,
        inactive: (res.data.organizationCount - res.data.activeOrganizationCount) || 0
      });
    } catch (err) {
      console.error('Error fetching organization stats:', err);
    }
  };

  const fetchOrgDetails = async (orgId) => {
    try {
      setLoading(true);
      
      // Fetch folders
      const folderRes = await axios.get(`/api/admin/organizations/${orgId}/folders`);
      setOrgFolders(folderRes.data);
      
      // Fetch templates
      const templateRes = await axios.get(`/api/admin/organizations/${orgId}/templates`);
      setOrgTemplates(templateRes.data);
      
      // Fetch users
      const userRes = await axios.get(`/api/admin/organizations/${orgId}/users`);
      setOrgUsers(userRes.data);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching organization details:', err);
      setError('Failed to load organization details. Please try again.');
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let result = [...organizations];
    
    // Apply search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(org => 
        org.name?.toLowerCase().includes(term) || 
        org.industry?.toLowerCase().includes(term) ||
        org.primaryContact?.name?.toLowerCase().includes(term) ||
        org.primaryContact?.email?.toLowerCase().includes(term)
      );
    }
    
    // Apply industry filter
    if (filters.industry !== 'all') {
      result = result.filter(org => org.industry === filters.industry);
    }
    
    // Apply status filter
    if (filters.status !== 'all') {
      result = result.filter(org => org.subscriptionStatus === filters.status);
    }
    
    // Apply size filter
    if (filters.size !== 'all') {
      result = result.filter(org => org.size === filters.size);
    }
    
    setFilteredOrganizations(result);
  };

  const handleSelectOrg = (org) => {
    setSelectedOrg(org);
    setView('detail');
    fetchOrgDetails(org._id);
  };

  const handleEditClick = () => {
    setFormData({
      name: selectedOrg.name || '',
      industry: selectedOrg.industry || '',
      size: selectedOrg.size || '',
      website: selectedOrg.website || '',
      phone: selectedOrg.phone || '',
      primaryContact: selectedOrg.primaryContact || {
        name: '',
        email: '',
        phone: ''
      },
      address: selectedOrg.address || {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      subscriptionTier: selectedOrg.subscriptionTier || 'Basic',
      subscriptionStatus: selectedOrg.subscriptionStatus || 'active',
      maxUsers: selectedOrg.maxUsers || 5
    });
    setIsEditing(true);
  };

  const handleCreateClick = () => {
    setFormData({
      name: '',
      industry: '',
      size: 'Small (1-50)',
      website: '',
      phone: '',
      primaryContact: {
        name: '',
        email: '',
        phone: ''
      },
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: '',
        country: ''
      },
      subscriptionTier: 'Basic',
      subscriptionStatus: 'active',
      maxUsers: 5
    });
    setIsCreating(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle nested objects (primaryContact and address)
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isCreating) {
        const res = await axios.post('/api/admin/organizations', formData);
        setOrganizations([...organizations, res.data]);
        setSuccessMessage('Organization created successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setIsCreating(false);
      } else if (isEditing) {
        const res = await axios.put(`/api/admin/organizations/${selectedOrg._id}`, formData);
        setOrganizations(organizations.map(org => 
          org._id === selectedOrg._id ? { ...org, ...res.data } : org
        ));
        setSelectedOrg({ ...selectedOrg, ...res.data });
        setSuccessMessage('Organization updated successfully');
        setTimeout(() => setSuccessMessage(''), 3000);
        setIsEditing(false);
      }
      fetchStats(); // Refresh stats after creating/updating
    } catch (err) {
      console.error('Error saving organization:', err);
      setError('Failed to save organization. Please try again.');
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setIsCreating(false);
    setError(null);
  };

  const handleDeleteConfirm = (org) => {
    setOrgToDelete(org);
    setShowDeleteConfirm(true);
  };

  const handleDeleteOrg = async () => {
    try {
      await axios.delete(`/api/admin/organizations/${orgToDelete._id}`);
      setOrganizations(organizations.filter(org => org._id !== orgToDelete._id));
      if (selectedOrg && selectedOrg._id === orgToDelete._id) {
        setSelectedOrg(null);
        setView('list');
      }
      setShowDeleteConfirm(false);
      setOrgToDelete(null);
      setSuccessMessage('Organization deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchStats(); // Refresh stats after deleting
    } catch (err) {
      console.error('Error deleting organization:', err);
      setError(err.response?.data?.msg || 'Failed to delete organization. Please try again.');
      setShowDeleteConfirm(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setOrgToDelete(null);
  };

  const handleBackToList = () => {
    setSelectedOrg(null);
    setView('list');
    setError(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({
      ...filters,
      [name]: value
    });
  };

  const handleExportData = () => {
    // Create CSV data from organizations
    const headers = ['Name', 'Industry', 'Size', 'Contact Name', 'Contact Email', 'Contact Phone', 'Status', 'Subscription Tier', 'Max Users'];
    
    const csvData = filteredOrganizations.map(org => [
      org.name,
      org.industry,
      org.size,
      org.primaryContact?.name || '',
      org.primaryContact?.email || '',
      org.primaryContact?.phone || '',
      org.subscriptionStatus,
      org.subscriptionTier,
      org.maxUsers
    ]);
    
    // Add headers to CSV data
    csvData.unshift(headers);
    
    // Convert to CSV string
    const csvString = csvData.map(row => row.join(',')).join('\n');
    
    // Create download link
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `organizations_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      industry: 'all',
      status: 'all',
      size: 'all'
    });
  };

  const renderStatusBadge = (status) => {
    let badgeClass = '';
    let icon = null;
    
    switch(status) {
      case 'active':
        badgeClass = 'admin-badge success';
        icon = <FaCheck />;
        break;
      case 'inactive':
        badgeClass = 'admin-badge danger';
        icon = <FaTimes />;
        break;
      case 'trial':
        badgeClass = 'admin-badge warning';
        icon = <FaExclamationTriangle />;
        break;
      case 'pending':
        badgeClass = 'admin-badge info';
        icon = <FaExclamationTriangle />;
        break;
      default:
        badgeClass = 'admin-badge warning';
        break;
    }
    
    return <span className={badgeClass}>{icon} {status}</span>;
  };

  const renderOrganizationForm = () => {
    return (
      <div className="admin-section">
        <div className="admin-header-with-back">
          <button className="admin-back-button" onClick={handleCancel}>
            <FaArrowLeft /> Back to Organizations
          </button>
          <h2 className="admin-section-title">
            <FaBuilding /> {isEditing ? 'Edit Organization' : 'Create New Organization'}
          </h2>
        </div>
        
        {error && (
          <div className="admin-error-message">
            <FaExclamationTriangle /> {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} ref={formRef}>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Organization Name*</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="admin-input"
                placeholder="Enter organization name"
                required
              />
            </div>
            
            <div className="admin-form-group">
              <label>Industry*</label>
              <select
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className="admin-select"
                required
              >
                <option value="">Select industry</option>
                <option value="Aerospace & Defense">Aerospace & Defense</option>
                <option value="Agriculture">Agriculture</option>
                <option value="Automotive">Automotive</option>
                <option value="Banking">Banking</option>
                <option value="Biotechnology">Biotechnology</option>
                <option value="Chemicals">Chemicals</option>
                <option value="College">College & University</option>
                <option value="Construction">Construction</option>
                <option value="Consulting">Consulting</option>
                <option value="Consumer Goods">Consumer Goods</option>
                <option value="Education">Education (K-12)</option>
                <option value="Energy">Energy & Utilities</option>
                <option value="Entertainment">Entertainment & Media</option>
                <option value="Financial">Financial Services</option>
                <option value="Food & Beverage">Food & Beverage</option>
                <option value="Government">Government</option>
                <option value="Healthcare">Healthcare</option>
                <option value="Hospitality">Hospitality & Tourism</option>
                <option value="Information Technology">Information Technology</option>
                <option value="Insurance">Insurance</option>
                <option value="Legal">Legal Services</option>
                <option value="Manufacturing">Manufacturing</option>
                <option value="Mining">Mining & Metals</option>
                <option value="Non-Profit">Non-Profit</option>
                <option value="Pharmaceuticals">Pharmaceuticals</option>
                <option value="Real Estate">Real Estate</option>
                <option value="Retail">Retail</option>
                <option value="Telecommunications">Telecommunications</option>
                <option value="Transportation">Transportation & Logistics</option>
                <option value="Other">Other</option>
              </select>
            </div>
          </div>
          
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Size</label>
              <select
                name="size"
                value={formData.size}
                onChange={handleChange}
                className="admin-select"
              >
                <option value="Small (1-50)">Small (1-50)</option>
                <option value="Medium (51-500)">Medium (51-500)</option>
                <option value="Large (501-5000)">Large (501-5000)</option>
                <option value="Enterprise (5000+)">Enterprise (5000+)</option>
              </select>
            </div>
            
            <div className="admin-form-group">
              <label>Website</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="admin-input"
                placeholder="https://example.com"
              />
            </div>
          </div>
          
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="admin-input"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            
            <div className="admin-form-group">
              <label>Subscription Tier</label>
              <select
                name="subscriptionTier"
                value={formData.subscriptionTier}
                onChange={handleChange}
                className="admin-select"
              >
                <option value="Basic">Basic</option>
                <option value="Professional">Professional</option>
                <option value="Enterprise">Enterprise</option>
              </select>
            </div>
          </div>
          
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Status</label>
              <select
                name="subscriptionStatus"
                value={formData.subscriptionStatus}
                onChange={handleChange}
                className="admin-select"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="trial">Trial</option>
                <option value="pending">Pending</option>
              </select>
            </div>
            
            <div className="admin-form-group">
              <label>Maximum Users</label>
              <input
                type="number"
                name="maxUsers"
                value={formData.maxUsers}
                onChange={handleChange}
                className="admin-input"
                min="1"
                max="1000"
              />
            </div>
          </div>
          
          <h3 className="admin-subsection-title">Primary Contact</h3>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Contact Name</label>
              <input
                type="text"
                name="primaryContact.name"
                value={formData.primaryContact.name}
                onChange={handleChange}
                className="admin-input"
                placeholder="John Doe"
              />
            </div>
            
            <div className="admin-form-group">
              <label>Contact Email</label>
              <input
                type="email"
                name="primaryContact.email"
                value={formData.primaryContact.email}
                onChange={handleChange}
                className="admin-input"
                placeholder="john.doe@example.com"
              />
            </div>
          </div>
          
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Contact Phone</label>
              <input
                type="tel"
                name="primaryContact.phone"
                value={formData.primaryContact.phone}
                onChange={handleChange}
                className="admin-input"
                placeholder="+1 (555) 123-4567"
              />
            </div>
            
            <div className="admin-form-group">
              {/* Spacer for alignment */}
            </div>
          </div>
          
          <h3 className="admin-subsection-title">Address</h3>
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Street</label>
              <input
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
                className="admin-input"
                placeholder="123 Main St"
              />
            </div>
            
            <div className="admin-form-group">
              <label>City</label>
              <input
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                className="admin-input"
                placeholder="New York"
              />
            </div>
          </div>
          
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>State/Province</label>
              <input
                type="text"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
                className="admin-input"
                placeholder="NY"
              />
            </div>
            
            <div className="admin-form-group">
              <label>ZIP/Postal Code</label>
              <input
                type="text"
                name="address.zipCode"
                value={formData.address.zipCode}
                onChange={handleChange}
                className="admin-input"
                placeholder="10001"
              />
            </div>
          </div>
          
          <div className="admin-form-row">
            <div className="admin-form-group">
              <label>Country</label>
              <input
                type="text"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
                className="admin-input"
                placeholder="United States"
              />
            </div>
            
            <div className="admin-form-group">
              {/* Spacer for alignment */}
            </div>
          </div>
          
          <div className="admin-form-actions">
            <button type="submit" className="admin-btn">
              <FaSave /> {isEditing ? 'Update Organization' : 'Create Organization'}
            </button>
            <button type="button" className="admin-btn secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  };

  // Predefined list of industries to match organization setup
  const industryOptions = [
    "Aerospace & Defense",
    "Agriculture",
    "Automotive",
    "Banking",
    "Biotechnology",
    "Chemicals",
    "College",
    "Construction",
    "Consulting",
    "Consumer Goods",
    "Education",
    "Energy",
    "Entertainment",
    "Financial",
    "Food & Beverage",
    "Government",
    "Healthcare",
    "Hospitality",
    "Information Technology",
    "Insurance",
    "Legal",
    "Manufacturing",
    "Mining",
    "Non-Profit",
    "Pharmaceuticals",
    "Real Estate",
    "Retail",
    "Telecommunications",
    "Transportation",
    "Other"
  ];

  const renderOrganizationList = () => {
    // Get unique industries from existing organizations for statistics
    const existingIndustries = [...new Set(organizations.map(org => org.industry).filter(Boolean))];
    
    return (
      <div className="admin-container">
        <div className="admin-header-with-back">
          <Link to="/admin" className="admin-back-button">
            <FaTachometerAlt /> Return to Admin Dashboard
          </Link>
          <h1 className="admin-title">Organization Management</h1>
        </div>
        
        {successMessage && (
          <div className="admin-success-message">
            <FaCheck /> {successMessage}
          </div>
        )}
        
        {/* Stats Section */}
        <div className="admin-stats-grid">
          <div className="admin-stat-card">
            <div className="admin-stat-icon">
              <FaBuilding />
            </div>
            <div className="admin-stat-content">
              <div className="admin-stat-label">Total Organizations</div>
              <div className="admin-stat-value">{stats.total}</div>
            </div>
          </div>
          
          <div className="admin-stat-card">
            <div className="admin-stat-icon">
              <FaCheck />
            </div>
            <div className="admin-stat-content">
              <div className="admin-stat-label">Active Organizations</div>
              <div className="admin-stat-value">{stats.active}</div>
            </div>
          </div>
          
          <div className="admin-stat-card">
            <div className="admin-stat-icon">
              <FaTimes />
            </div>
            <div className="admin-stat-content">
              <div className="admin-stat-label">Inactive Organizations</div>
              <div className="admin-stat-value">{stats.inactive}</div>
            </div>
          </div>
        </div>
        
        {/* Filter Section */}
        <div className="admin-section">
          <div className="admin-section-title">
            <FaFilter /> Filter Organizations
          </div>
          
          <div className="admin-filter-panel">
            <div className="admin-filter-row">
              <div className="admin-filter-group">
                <label>Search</label>
                <div className="admin-search-input">
                  <input
                    type="text"
                    placeholder="Search by name, industry, or contact..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    className="admin-input"
                  />
                  <FaSearch className="admin-search-icon" />
                </div>
              </div>
              
              <div className="admin-filter-group">
                <label>Industry</label>
                <select
                  name="industry"
                  value={filters.industry}
                  onChange={handleFilterChange}
                  className="admin-select"
                >
                  <option value="all">All Industries</option>
                  {industryOptions.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>
              
              <div className="admin-filter-group">
                <label>Status</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="admin-select"
                >
                  <option value="all">All Statuses</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="trial">Trial</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
            
            <div className="admin-filter-row">
              <div className="admin-filter-group">
                <label>Size</label>
                <select
                  name="size"
                  value={filters.size}
                  onChange={handleFilterChange}
                  className="admin-select"
                >
                  <option value="all">All Sizes</option>
                  <option value="Small (1-50)">Small (1-50)</option>
                  <option value="Medium (51-500)">Medium (51-500)</option>
                  <option value="Large (501-5000)">Large (501-5000)</option>
                  <option value="Enterprise (5000+)">Enterprise (5000+)</option>
                </select>
              </div>
              
              <div className="admin-filter-actions">
                <button className="admin-btn" onClick={clearFilters}>
                  Clear Filters
                </button>
                <button className="admin-btn" onClick={handleExportData}>
                  <FaDownload /> Export Data
                </button>
                <button className="admin-btn" onClick={handleCreateClick}>
                  <FaPlus /> Add Organization
                </button>
              </div>
            </div>
            
            <div className="admin-filter-status">
              <p>
                <FaBuilding /> Showing {filteredOrganizations.length} of {organizations.length} organizations
              </p>
            </div>
          </div>
        </div>
        
        {/* Organizations Table */}
        <div className="admin-section">
          <div className="admin-section-title">
            <FaBuilding /> Organizations
          </div>
          
          <div className="admin-table-container">
            <table className="admin-data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Industry</th>
                  <th>Size</th>
                  <th>Primary Contact</th>
                  <th>Status</th>
                  <th>Subscription</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrganizations.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="admin-no-data">No organizations found</td>
                  </tr>
                ) : (
                  filteredOrganizations.map(org => (
                    <tr key={org._id}>
                      <td>{org.name}</td>
                      <td>{org.industry}</td>
                      <td>{org.size}</td>
                      <td>
                        {org.primaryContact?.name ? (
                          <>
                            {org.primaryContact.name}<br />
                            <small>{org.primaryContact.email}</small>
                          </>
                        ) : '-'}
                      </td>
                      <td>{renderStatusBadge(org.subscriptionStatus)}</td>
                      <td>{org.subscriptionTier}</td>
                      <td>
                        <div className="admin-actions">
                          <button 
                            className="admin-action-btn" 
                            title="View Details"
                            onClick={() => handleSelectOrg(org)}
                          >
                            <FaArrowRight />
                          </button>
                          <button 
                            className="admin-action-btn" 
                            title="Edit Organization"
                            onClick={() => {
                              setSelectedOrg(org);
                              handleEditClick();
                            }}
                          >
                            <FaEdit />
                          </button>
                          <button 
                            className="admin-action-btn delete" 
                            title="Delete Organization"
                            onClick={() => handleDeleteConfirm(org)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="admin-modal-overlay">
            <div className="admin-modal">
              <div className="modal-header">
                <h3>Confirm Delete</h3>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete the organization <strong>{orgToDelete?.name}</strong>?</p>
                <p className="text-danger">This action cannot be undone and will remove all associated data.</p>
                
                <div className="admin-form-actions">
                  <button className="admin-btn admin-btn-danger" onClick={handleDeleteOrg}>
                    <FaTrash /> Delete Organization
                  </button>
                  <button className="admin-btn secondary" onClick={handleCancelDelete}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderOrganizationDetail = () => {
    return (
      <div className="admin-container">
        <div className="admin-header-with-back">
          <button className="admin-back-button" onClick={handleBackToList}>
            <FaArrowLeft /> Back to Organizations
          </button>
          <h1 className="admin-title">{selectedOrg.name}</h1>
        </div>
        
        {successMessage && (
          <div className="admin-success-message">
            <FaCheck /> {successMessage}
          </div>
        )}
        
        {error && (
          <div className="admin-error-message">
            <FaExclamationTriangle /> {error}
          </div>
        )}
        
        <div className="admin-section">
          <div className="admin-section-header">
            <div className="admin-title-area">
              <h2 className="admin-section-title">
                <FaBuilding /> Organization Details
              </h2>
              <p className="admin-section-description">
                View and manage details for {selectedOrg.name}
              </p>
            </div>
            <div className="admin-header-actions">
              <button className="admin-btn" onClick={handleEditClick}>
                <FaEdit /> Edit Organization
              </button>
              <button className="admin-btn admin-btn-danger" onClick={() => handleDeleteConfirm(selectedOrg)}>
                <FaTrash /> Delete Organization
              </button>
            </div>
          </div>
          
          <div className="admin-tabs">
            <button 
              className={`admin-tab ${view === 'detail' ? 'active' : ''}`}
              onClick={() => setView('detail')}
            >
              <FaBuilding /> Details
            </button>
            <button 
              className={`admin-tab ${view === 'users' ? 'active' : ''}`}
              onClick={() => setView('users')}
            >
              <FaUsers /> Users ({orgUsers.length})
            </button>
            <button 
              className={`admin-tab ${view === 'folders' ? 'active' : ''}`}
              onClick={() => setView('folders')}
            >
              <FaFolder /> Folders ({orgFolders.length})
            </button>
            <button 
              className={`admin-tab ${view === 'templates' ? 'active' : ''}`}
              onClick={() => setView('templates')}
            >
              <FaClipboardList /> Templates ({orgTemplates.length})
            </button>
          </div>
          
          {view === 'detail' && (
            <div className="admin-tab-content">
              <div className="admin-detail-grid">
                <div className="admin-detail-card">
                  <h3 className="admin-detail-card-title">Organization Information</h3>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Name</div>
                    <div className="admin-detail-value">{selectedOrg.name}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Industry</div>
                    <div className="admin-detail-value">{selectedOrg.industry || '-'}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Size</div>
                    <div className="admin-detail-value">{selectedOrg.size || '-'}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Website</div>
                    <div className="admin-detail-value">
                      {selectedOrg.website ? (
                        <a href={selectedOrg.website} target="_blank" rel="noopener noreferrer">
                          {selectedOrg.website}
                        </a>
                      ) : '-'}
                    </div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Phone</div>
                    <div className="admin-detail-value">{selectedOrg.phone || '-'}</div>
                  </div>
                </div>
                
                <div className="admin-detail-card">
                  <h3 className="admin-detail-card-title">Subscription Details</h3>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Status</div>
                    <div className="admin-detail-value">{renderStatusBadge(selectedOrg.subscriptionStatus)}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Tier</div>
                    <div className="admin-detail-value">{selectedOrg.subscriptionTier || 'Basic'}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Maximum Users</div>
                    <div className="admin-detail-value">{selectedOrg.maxUsers || 5}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Expiry Date</div>
                    <div className="admin-detail-value">
                      {selectedOrg.subscriptionExpiry ? (
                        new Date(selectedOrg.subscriptionExpiry).toLocaleDateString()
                      ) : '-'}
                    </div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Created</div>
                    <div className="admin-detail-value">
                      {selectedOrg.createdAt ? (
                        new Date(selectedOrg.createdAt).toLocaleDateString()
                      ) : '-'}
                    </div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Last Updated</div>
                    <div className="admin-detail-value">
                      {selectedOrg.updatedAt ? (
                        new Date(selectedOrg.updatedAt).toLocaleDateString()
                      ) : '-'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="admin-detail-grid">
                <div className="admin-detail-card">
                  <h3 className="admin-detail-card-title">Primary Contact</h3>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Name</div>
                    <div className="admin-detail-value">{selectedOrg.primaryContact?.name || '-'}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Email</div>
                    <div className="admin-detail-value">
                      {selectedOrg.primaryContact?.email ? (
                        <a href={`mailto:${selectedOrg.primaryContact.email}`}>
                          {selectedOrg.primaryContact.email}
                        </a>
                      ) : '-'}
                    </div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Phone</div>
                    <div className="admin-detail-value">{selectedOrg.primaryContact?.phone || '-'}</div>
                  </div>
                </div>
                
                <div className="admin-detail-card">
                  <h3 className="admin-detail-card-title">Address</h3>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Street</div>
                    <div className="admin-detail-value">{selectedOrg.address?.street || '-'}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">City</div>
                    <div className="admin-detail-value">{selectedOrg.address?.city || '-'}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">State/Province</div>
                    <div className="admin-detail-value">{selectedOrg.address?.state || '-'}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">ZIP/Postal Code</div>
                    <div className="admin-detail-value">{selectedOrg.address?.zipCode || '-'}</div>
                  </div>
                  <div className="admin-detail-item">
                    <div className="admin-detail-label">Country</div>
                    <div className="admin-detail-value">{selectedOrg.address?.country || '-'}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {view === 'users' && (
            <div className="admin-tab-content">
              <div className="admin-section-header">
                <h3>Organization Users</h3>
                <button className="admin-btn">
                  <FaPlus /> Add User
                </button>
              </div>
              
              <div className="admin-table-container">
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgUsers.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="admin-no-data">No users found</td>
                      </tr>
                    ) : (
                      orgUsers.map(user => (
                        <tr key={user._id}>
                          <td>{user.name}</td>
                          <td>{user.email}</td>
                          <td>{user.role}</td>
                          <td>{renderStatusBadge(user.subscriptionStatus || 'active')}</td>
                          <td>
                            <div className="admin-actions">
                              <button 
                                className="admin-action-btn" 
                                title="Edit User"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                className="admin-action-btn delete" 
                                title="Remove User"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {view === 'folders' && (
            <div className="admin-tab-content">
              <div className="admin-section-header">
                <h3>Organization Folders</h3>
                <button className="admin-btn">
                  <FaPlus /> Add Folder
                </button>
              </div>
              
              <div className="admin-table-container">
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Templates</th>
                      <th>Users</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgFolders.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="admin-no-data">No folders found</td>
                      </tr>
                    ) : (
                      orgFolders.map(folder => (
                        <tr key={folder._id}>
                          <td>{folder.name}</td>
                          <td>{folder.description || '-'}</td>
                          <td>{folder.templateCount || 0}</td>
                          <td>{folder.userCount || 0}</td>
                          <td>
                            <div className="admin-actions">
                              <button 
                                className="admin-action-btn" 
                                title="Edit Folder"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                className="admin-action-btn delete" 
                                title="Delete Folder"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          
          {view === 'templates' && (
            <div className="admin-tab-content">
              <div className="admin-section-header">
                <h3>Organization Templates</h3>
                <button className="admin-btn">
                  <FaPlus /> Assign Template
                </button>
              </div>
              
              <div className="admin-table-container">
                <table className="admin-data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Industry</th>
                      <th>Questions</th>
                      <th>Folder</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orgTemplates.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="admin-no-data">No templates found</td>
                      </tr>
                    ) : (
                      orgTemplates.map(template => (
                        <tr key={template._id}>
                          <td>{template.name}</td>
                          <td>{template.industry}</td>
                          <td>{template.questions?.length || 0}</td>
                          <td>{template.folder?.name || '-'}</td>
                          <td>
                            <div className="admin-actions">
                              <button 
                                className="admin-action-btn" 
                                title="Edit Template Assignment"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                className="admin-action-btn delete" 
                                title="Remove Template"
                              >
                                <FaTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
        
        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="admin-modal-overlay">
            <div className="admin-modal">
              <div className="modal-header">
                <h3>Confirm Delete</h3>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete the organization <strong>{orgToDelete?.name}</strong>?</p>
                <p className="text-danger">This action cannot be undone and will remove all associated data.</p>
                
                <div className="admin-form-actions">
                  <button className="admin-btn admin-btn-danger" onClick={handleDeleteOrg}>
                    <FaTrash /> Delete Organization
                  </button>
                  <button className="admin-btn secondary" onClick={handleCancelDelete}>
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading && !isEditing && !isCreating && !selectedOrg) {
    return (
      <div className="admin-container">
        <div className="admin-header-with-back">
          <Link to="/admin" className="admin-back-button">
            <FaTachometerAlt /> Return to Admin Dashboard
          </Link>
          <h1 className="admin-title">Organization Management</h1>
        </div>
        <div className="admin-loading">
          <div className="admin-loading-spinner"></div>
          <p>Loading organizations...</p>
        </div>
      </div>
    );
  }

  if (error && !isEditing && !isCreating && !selectedOrg) {
    return (
      <div className="admin-container">
        <div className="admin-header-with-back">
          <Link to="/admin" className="admin-back-button">
            <FaTachometerAlt /> Return to Admin Dashboard
          </Link>
          <h1 className="admin-title">Organization Management</h1>
        </div>
        <div className="admin-error-message">
          <FaExclamationTriangle /> {error}
        </div>
      </div>
    );
  }

  if (isEditing || isCreating) {
    return renderOrganizationForm();
  }
  
  if (selectedOrg) {
    return renderOrganizationDetail();
  }
  
  return renderOrganizationList();
};

export default OrganizationManagement;
